import type { FuseResultWithScore } from 'fuse.js';
import { colorSimilarity } from './colorSimilarity';
import { cosineSimilarity } from './cosineSimilarity';
import type { Embedding, ThemeEmbeddings } from './search.types';

export const SIMILARITY_THRESHOLD = 0.44;
export const SIMILARITY_TIEBREAK_THRESHOLD = SIMILARITY_THRESHOLD * 0.5;
const SIMILARITY_TIE_EPSILON = 1e-9;

type ScoreAndFilterOptions<T, F = T> = Readonly<{
  termEmbedding: Embedding;
  themes: Map<string, ThemeEmbeddings>;
  embeddingMap: Map<string, Embedding>;
  getThemeId: (item: T) => string;
  fuseResults: ReadonlyArray<FuseResultWithScore<F>>;
  getFuseItemThemeId: (item: F) => string;
  threshold: number;
  wantsFree?: boolean;
  isFreeItem?: (item: T) => boolean;
  getItemCustoms?: (item: T) => ReadonlyArray<Embedding>;
}>;

export function scoreAndFilterByEmbedding<T, F = T>(
  items: ReadonlyArray<T>,
  { termEmbedding, themes, embeddingMap, getThemeId, fuseResults, getFuseItemThemeId, threshold, wantsFree = false, isFreeItem, getItemCustoms }: ScoreAndFilterOptions<T, F>,
): ReadonlyArray<T> {
  const white = embeddingMap.get('color: white');
  const beige = embeddingMap.get('color: beige');

  const lexicalScores = new Map(fuseResults.map(r => [getFuseItemThemeId(r.item), 1 - (r.score ?? 1)]));

  const similarityMap = new Map<string, number>();
  const similarityTiebreakMap = new Map<string, number>();

  for (const item of items) {
    const themeId = getThemeId(item);
    const theme = themes.get(themeId);

    const similarityLexical = lexicalScores.get(themeId) ?? 0;
    const scoresConcept = (theme?.concepts ?? []).map(e => cosineSimilarity(termEmbedding, e));
    const similarityConcept = Math.max(0, ...scoresConcept);
    // Sum of above-floor concept scores, used to break ties when overall similarity is equal
    const conceptSumScore = scoresConcept.reduce((sum, s) => sum + Math.max(0, s - SIMILARITY_TIEBREAK_THRESHOLD), 0);
    const itemCustoms = getItemCustoms?.(item) ?? [];
    const allCustoms = [...(theme?.customs ?? []), ...itemCustoms];
    const similarityCustom = Math.max(0, ...allCustoms.map(e => cosineSimilarity(termEmbedding, e)));
    const similarityColor = colorSimilarity(termEmbedding, theme?.colors ?? [], white, beige);
    const isFree = wantsFree && (isFreeItem?.(item) ?? false);

    const similarity = Math.max(
      similarityLexical * 1.01, // A perfect lexical match ranks above all else. *1.01 to tie-break.
      similarityConcept,
      similarityColor,
      similarityCustom,
      isFree ? 1 : 0,
    );

    similarityMap.set(themeId, similarity);
    similarityTiebreakMap.set(themeId, conceptSumScore);
  }

  const filtered = items.filter(item => (similarityMap.get(getThemeId(item)) ?? 0) >= threshold);
  const sorted = [...filtered].sort((a, b) => {
    const scoreA = similarityMap.get(getThemeId(a)) ?? 0;
    const scoreB = similarityMap.get(getThemeId(b)) ?? 0;
    const diff = scoreB - scoreA;

    // Precedence 1: primary sort on final similarity. Decides outright unless
    // scores are tied — i.e. multiple items resolve their max via the same
    // embedding (e.g. photo-tagged themes all maxing on the shared `custom`
    // cosine).
    if (Math.abs(diff) >= SIMILARITY_TIE_EPSILON) return diff;

    // Precedence 2: tiebreak by token-level name match. Fuse is configured with
    // `tokenize: true`, so `lexicalScores` already reflects per-token hits on
    // the template name. A name that literally contains a query word beats one
    // that only matches via embeddings.
    const lexicalDiff = (lexicalScores.get(getThemeId(b)) ?? 0) - (lexicalScores.get(getThemeId(a)) ?? 0);
    if (lexicalDiff !== 0) return lexicalDiff;

    // Precedence 3: tiebreak by concept breadth. `conceptSum` rewards themes
    // whose style keywords are broadly (if weakly) related to the query,
    // preferring them over themes that got lucky on one factor but are
    // otherwise off-topic.
    return (similarityTiebreakMap.get(getThemeId(b)) ?? 0) - (similarityTiebreakMap.get(getThemeId(a)) ?? 0);
  });

  return sorted;
}
